import React, { useState, useEffect, useRef } from 'react';
import { Line } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler
} from 'chart.js';
import { Box } from '@mui/material';

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler
);

const GraficaTemperaturaMachine11 = ({ socket }) => {
    const [chartData, setChartData] = useState({
        labels: [],
        datasets: [
            {
                label: 'Temperatura',
                data: [],
                borderColor: '#B8F1FF',
                backgroundColor: 'rgba(184, 241, 255, 0.3)',
                borderWidth: 2,
                fill: true,
                tension: 0.6,
                pointRadius: 0
            }
        ]
    });

    const dataBufferRef = useRef({
        labels: [],
        temp1: [],
        timestamps: []
    });
    const datosHistoricosRef = useRef(false);

    // Capacidad: 2.5 horas = 150 minutos = 900 lecturas (si llega cada 10 segundos)
    const MAX_POINTS = 900;
    const SESSION_STORAGE_KEY = 'machine11_temperature_data';
    // eslint-disable-next-line no-unused-vars
    const TREINTA_MINUTOS = 30 * 60 * 1000;

    // eslint-disable-next-line no-unused-vars
    const expandirLectura = (lectura, indexEnArray) => {
        const puntos = [];
        const endTime = new Date(lectura.fecha);
        const startTime = new Date(endTime.getTime() - 10 * 60 * 1000);
        
        for (let i = 0; i < 60; i++) {
            const time = new Date(startTime.getTime() + i * 10000);
            const label = time.toLocaleTimeString('es-CO', {
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit'
            });
            const variacion = (Math.random() - 0.5) * 0.5;
            puntos.push({
                label,
                temp: lectura.temperatura1 + variacion,
                timestamp: time.getTime()
            });
        }
        return puntos;
    };

    const cargarHistorialAntesDe = async (fechaReferencia) => {
        try {
            console.log(`[Machine11] ⭐ Cargando 2h antes de: ${new Date(fechaReferencia).toLocaleString('es-CO')}`);
            
            const url = `${process.env.REACT_APP_API_URL || 'http://31.97.100.31:5002/api'}/machine11?antes=${fechaReferencia}`;
            console.log('[Machine11] 🌐 URL:', url);
            
            const response = await fetch(url);
            console.log('[Machine11] 📡 Response status:', response.status);
            
            const data = await response.json();
            console.log('[Machine11] 📦 Data recibida:', data);
            console.log('[Machine11] 🔍 Tiene lecturas?', data?.lecturas?.length);
            console.log('[Machine11] 🔍 Es array?', Array.isArray(data));
            
            if (data && data.lecturas && data.lecturas.length > 0) {
                console.log(`[Machine11] Recibidas ${data.lecturas.length} lecturas del backend`);
                
                const puntosExpandidos = [];
                
                // Interpolar entre lecturas consecutivas
                for (let i = 0; i < data.lecturas.length; i++) {
                    const lecturaActual = data.lecturas[i];
                    const lecturaSiguiente = data.lecturas[i + 1];
                    
                    const endTime = new Date(lecturaActual.fecha);
                    const startTime = new Date(endTime.getTime() - 10 * 60 * 1000);
                    
                    const tempInicial = lecturaActual.temperatura1;
                    const tempFinal = lecturaSiguiente ? lecturaSiguiente.temperatura1 : tempInicial;
                    
                    for (let j = 0; j < 60; j++) {
                        const time = new Date(startTime.getTime() + j * 10000);
                        const label = time.toLocaleTimeString('es-CO', {
                            hour: '2-digit',
                            minute: '2-digit',
                            second: '2-digit'
                        });
                        
                        // Interpolación lineal entre temperatura inicial y final
                        const progreso = j / 60;
                        const tempInterpolada = tempInicial + (tempFinal - tempInicial) * progreso;
                        
                        puntosExpandidos.push({
                            label,
                            temp: tempInterpolada,
                            timestamp: time.getTime()
                        });
                    }
                }
                
                // PREPEND (agregar al inicio)
                dataBufferRef.current = {
                    labels: [...puntosExpandidos.map(p => p.label), ...dataBufferRef.current.labels],
                    temp1: [...puntosExpandidos.map(p => p.temp), ...dataBufferRef.current.temp1],
                    timestamps: [...puntosExpandidos.map(p => p.timestamp), ...dataBufferRef.current.timestamps]
                };
                
                // Limitar a MAX_POINTS
                if (dataBufferRef.current.labels.length > MAX_POINTS) {
                    dataBufferRef.current.labels = dataBufferRef.current.labels.slice(-MAX_POINTS);
                    dataBufferRef.current.temp1 = dataBufferRef.current.temp1.slice(-MAX_POINTS);
                    dataBufferRef.current.timestamps = dataBufferRef.current.timestamps.slice(-MAX_POINTS);
                }
                
                actualizarGrafica();
                console.log(`[Machine11] Historial cargado: ${puntosExpandidos.length} puntos`);
            }
        } catch (error) {
            console.error('[Machine11] Error cargando historial:', error);
        }
    };

    // eslint-disable-next-line no-unused-vars
    const cargarDatosIniciales = async () => {
        try {
            console.log('[Machine11] Fetching desde API...');
            const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://31.97.100.31:5002/api'}/machine11`);
            if (!response.ok) throw new Error('Error al obtener datos');
            
            const data = await response.json();
            console.log('[Machine11] Datos recibidos:', data.lecturas ? data.lecturas.length + ' lecturas' : 'sin lecturas');
            
            if (data && data.lecturas && data.lecturas.length > 0) {
                const ultimasLecturas = data.lecturas.slice(-12);
                console.log(`[Machine11] Expandiendo ${ultimasLecturas.length} lecturas...`);
                const puntosExpandidos = [];
                ultimasLecturas.forEach((lectura, index) => {
                    console.log(`[Machine11] Lectura ${index}: fecha=${lectura.fecha}, temp=${lectura.temperatura1}`);
                    const puntos = expandirLectura(lectura, index);
                    if (index === 0) {
                        console.log(`[Machine11] Primeros 3 labels generados:`, puntos.slice(0, 3).map(p => p.label));
                    }
                    puntosExpandidos.push(...puntos);
                });
                
                console.log(`[Machine11] Total puntos expandidos: ${puntosExpandidos.length}`);
                console.log(`[Machine11] Rango de fechas: ${puntosExpandidos[0]?.label} a ${puntosExpandidos[puntosExpandidos.length-1]?.label}`);
                const puntosFinales = puntosExpandidos.slice(-MAX_POINTS);
                
                dataBufferRef.current = {
                    labels: puntosFinales.map(p => p.label),
                    temp1: puntosFinales.map(p => p.temp),
                    timestamps: puntosFinales.map(p => p.timestamp)
                };
                
                sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(dataBufferRef.current));
                console.log('[Machine11] Datos guardados en sessionStorage');
                actualizarGrafica();
            } else {
                console.log('[Machine11] No hay lecturas disponibles en MongoDB');
            }
        } catch (error) {
            console.error('[Machine11] Error cargando datos iniciales:', error);
        }
    };

    const actualizarGrafica = () => {
        const filteredLabels = dataBufferRef.current.timestamps.map((ts) => {
            if (!ts) return '';
            const d = new Date(ts);
            return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
        });

        setChartData({
            labels: filteredLabels,
            datasets: [
                {
                    label: 'Temperatura',
                    data: dataBufferRef.current.temp1,
                    borderColor: '#B8F1FF',
                    backgroundColor: 'rgba(184, 241, 255, 0.3)',
                    borderWidth: 2,
                    fill: true,
                    tension: 0.6,
                    pointRadius: 0
                }
            ]
        });
    };

    useEffect(() => {
        dataBufferRef.current = { labels: [], temp1: [], timestamps: [] };
        datosHistoricosRef.current = true;
        cargarHistorialAntesDe(Date.now());
    }, []);

    useEffect(() => {
        if (!socket) return;

        const handleMachine11Data = (data) => {
            if (data.maquinaId !== 'Machine11') return;

            const now = new Date();
            dataBufferRef.current.labels.push(now.toLocaleTimeString('es-CO', {
                hour: '2-digit', minute: '2-digit', second: '2-digit'
            }));
            dataBufferRef.current.temp1.push(data.temperatura1 ?? 0);
            dataBufferRef.current.timestamps.push(now.getTime());

            if (dataBufferRef.current.labels.length > MAX_POINTS) {
                dataBufferRef.current.labels.shift();
                dataBufferRef.current.temp1.shift();
                dataBufferRef.current.timestamps.shift();
            }

            actualizarGrafica();
        };

        socket.on('mqttMachine11', handleMachine11Data);

        return () => {
            socket.off('mqttMachine11', handleMachine11Data);
        };
    }, [socket]);

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        layout: {
            padding: {
                left: 5,
                right: 10,
                top: 10,
                bottom: 5
            }
        },
        scales: {
            x: {
                display: true,
                ticks: {
                    color: '#aaa',
                    maxRotation: 0,
                    minRotation: 0,
                    autoSkip: true,
                    maxTicksLimit: 8,
                    font: {
                        size: 9
                    }
                },
                grid: {
                    drawOnChartArea: false,
                    drawTicks: true,
                    tickLength: 4,
                    tickColor: '#666'
                },
                title: {
                    display: true,
                    text: 'Tiempo',
                    color: '#aaa',
                    font: {
                        size: 10
                    }
                }
            },
            y: {
                display: true,
                ticks: {
                    color: '#aaa',
                    font: {
                        size: 9
                    },
                    stepSize: 20
                },
                grid: {
                    display: false
                },
                min: 0,
                max: 150,
                title: {
                    display: true,
                    text: 'Temp °C',
                    color: '#aaa',
                    font: {
                        size: 10
                    }
                }
            }
        },
        plugins: {
            legend: {
                display: false
            },
            tooltip: {
                enabled: true,
                mode: 'index',
                intersect: false,
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                titleColor: '#fff',
                bodyColor: '#fff',
                borderColor: '#B8F1FF',
                borderWidth: 1,
                padding: 8,
                displayColors: true
            }
        },
        interaction: {
            mode: 'nearest',
            axis: 'x',
            intersect: false
        }
    };

    return (
        <Box sx={{ width: '100%', height: '100%', position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}>
            <Line data={chartData} options={options} />
        </Box>
    );
};

export default GraficaTemperaturaMachine11;
